import React, {useEffect, useRef, useState} from 'react';
import axios from 'axios';
import Env from '../../../../util/env';
import {getJWT} from '../../../../common/util/util-auth';
import Resources from '../../../../data/services/resources';
import {processResponse} from '../../../../data/services/api-util';
import OcrSplitDialog from '../../../../common/components/modal/ocr-split-dialog';
import ModalConfirm from '../../../../common/components/modal/modal-confirm';
import BillExpenseOcrForm, {getDefaultAccount, getExpenseItemField, getHeaderFields, itemsFromExtraction, validateOcrForm} from './bill-expense-ocr-form';
import {matchVendor} from './vendor-match';
import {matchProductServiceCandidate, matchTaxRateCandidate} from './item-match-helpers';
import {catScaleRowsFromPreview, catScaleWarningSummary, isCsvFile} from './cat-scale-import';
import {CSV_PREVIEW_ROW_LIMIT, parseCsv, readFileAsText} from './csv-preview';
import CsvPreviewTable from './csv-preview-table';
import InfoParagraph from '../../../../common/components/info-paragraph';

async function searchQuick(resource, query) {
    const response = await axios.get(
        Env.getApiUrl('api/' + resource, {query, offset: 0, limit: 1}),
        {headers: {Authorization: `Bearer ${getJWT().access_token}`}}
    );
    const result = processResponse(response);
    return result && result.status === 0 ? (result.data.list[0] ?? null) : null;
}

async function fetchTaxCandidates() {
    const response = await axios.get(
        Env.getApiUrl('api/' + Resources.AccountingTaxes, {offset: 0, limit: 50}),
        {headers: {Authorization: `Bearer ${getJWT().access_token}`}}
    );
    const result = processResponse(response);
    return result && result.status === 0 ? result.data.list : [];
}

async function enrichItems(items) {
    const taxCandidates = await fetchTaxCandidates();

    return Promise.all((items ?? []).map(async (item) => {
        const productCandidates = item.ProductServiceID
            ? await axios.get(
                Env.getApiUrl('api/' + Resources.ProductsServices, {query: item.ProductServiceID, offset: 0, limit: 5}),
                {headers: {Authorization: `Bearer ${getJWT().access_token}`}}
            ).then((response) => {
                const result = processResponse(response);
                return result && result.status === 0 ? result.data.list : [];
            })
            : [];

        const matchedProductService = matchProductServiceCandidate(item.ProductServiceID ?? '', productCandidates);
        const matchedTax = matchTaxRateCandidate(Number(item.TaxAmount) || 0, Number(item.Amount) || 0, taxCandidates);

        return {...item, matchedProductService, matchedTax};
    }));
}

export default function BillExpenseOcrDialog({show, file, onClose, onSave, translate, defaults}) {
    const [isLoading, setIsLoading] = useState(() => false);
    const [isError, setIsError] = useState(() => false);
    const [noVendorFoundDialog, setNoVendorFoundDialog] = useState(() => false);

    // The form's state lives here, not inside BillExpenseOcrForm: OcrSplitDialog only renders the
    // form panel while it is visible, so toggling that panel off and on unmounts/remounts the form.
    // Owning mode/fields/items (and the validation error) here is what keeps the user's data entry
    // across that cycle, and what lets Save validate without the form being mounted at all.
    const [mode, setMode] = useState(() => 'bill');
    const [fields, setFields] = useState(() => null);
    const [items, setItems] = useState(() => null);
    const [errorMessage, setErrorMessage] = useState(() => null);

    // Set only on the spreadsheet path: the parsed rows shown in place of the document
    // preview, and a flag for the driver column the AI path has no use for.
    const [csvRows, setCsvRows] = useState(() => null);
    const [isCatScale, setIsCatScale] = useState(() => false);

    const abortControllerRef = useRef(null);

    /** Data Events
     ================================================================= */
    function extractDocument() {
        if (!file) {
            return;
        }

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        setIsLoading(true);
        setIsError(false);
        setFields(null);
        setItems(null);
        setErrorMessage(null);
        setCsvRows(null);
        setIsCatScale(false);

        // A spreadsheet is not something the document AI reads. It goes to excel/preview,
        // which recognises the format from the file's own header row and returns line items
        // already shaped for a bill. Everything else is a scan or a PDF: AI extraction.
        if (isCsvFile(file)) {
            extractSpreadsheet(controller);
            return;
        }

        const formData = new FormData();
        formData.append('Document', file);

        axios.post(
            Env.getDocumentAIApiUrl('api/' + Resources.AiExtractBill, {}),
            formData,
            {headers: {Authorization: `Bearer ${getJWT().access_token}`}, signal: controller.signal}
        )
            .then(async (response) => {
                const data = response?.data?.data;
                if (!data) {
                    setIsError(true);
                    setIsLoading(false);
                    return;
                }

                const [vendorResult, enrichedItems] = await Promise.all([
                    data.VendorName?.trim()
                        ? matchVendor(data.VendorName, {
                            searchOrganizations: (name) => searchQuick(Resources.OrganizationsQuick, name),
                            searchContacts: (name) => searchQuick(Resources.ContactsQuick, name),
                        })
                        : Promise.resolve({type: null, match: null}),
                    enrichItems(data.Items),
                ]);

                const initialOrganization = vendorResult.type === 'organization' ? vendorResult.match : null;

                // Populated before isLoading is cleared, so the form panel never renders without them.
                setFields(getHeaderFields(data, vendorResult, translate));
                setItems(itemsFromExtraction(enrichedItems, translate, getDefaultAccount(initialOrganization, defaults)));

                setNoVendorFoundDialog(!vendorResult.type);
                setIsLoading(false);
            })
            .catch((error) => {
                if (!axios.isCancel(error)) {
                    setIsError(true);
                    setIsLoading(false);
                }
            });
    }

    function extractSpreadsheet(controller) {
        const formData = new FormData();
        formData.append('Document', file);

        // The company's own expense/advance accounts. The server verifies both against this
        // company's chart of accounts and falls back to these same settings if they do not
        // check out, so sending them is a convenience, not a trust boundary.
        if (defaults?.ExpenseAccountID) {
            formData.append('ExpensesAccountID', defaults.ExpenseAccountID);
        }
        if (defaults?.AdvancesAccountID) {
            formData.append('AdvancesAccountID', defaults.AdvancesAccountID);
        }

        Promise.all([
            axios.post(
                Env.getApiUrl('api/' + Resources.ExcelPreview, {}),
                formData,
                {headers: {Authorization: `Bearer ${getJWT().access_token}`}, signal: controller.signal}
            ),
            // Only for the preview pane, so a failure here must not fail the import.
            readFileAsText(file).catch(() => ''),
        ])
            .then(([response, text]) => {
                const result = processResponse(response);
                const data = result && result.status === 0 ? result.data : null;

                // No recognised format. Not forwarded to the AI endpoint, which expects a
                // scan or a PDF — the user is told instead, and can retry with another file.
                if (!data?.Format) {
                    setIsError(true);
                    setIsLoading(false);
                    return;
                }

                setCsvRows(parseCsv(text, CSV_PREVIEW_ROW_LIMIT + 1));
                setIsCatScale(true);

                // No vendor in a scale export — the file says which carrier it is, not who is
                // billing. The user picks it; the header field is required, so Save enforces it.
                setFields(getHeaderFields({}, {type: null, match: null}, translate));
                setItems(catScaleRowsFromPreview(data.Items, defaults, data.PayTo)
                    .map((row) => getExpenseItemField(row, translate, null)));

                setIsLoading(false);
            })
            .catch((error) => {
                if (!axios.isCancel(error)) {
                    setIsError(true);
                    setIsLoading(false);
                }
            });
    }

    /** UI Events
     ================================================================= */
    function handleSave() {
        // Validation reads the state owned here, so Save behaves identically whether or not the
        // shell's form panel is currently mounted.
        const {
            fields: validatedFields,
            items: validatedItems,
            errorMessage: nextErrorMessage,
            result,
        } = validateOcrForm(mode, fields, items, translate);

        setFields(validatedFields);
        setItems(validatedItems);
        setErrorMessage(nextErrorMessage);

        if (result) {
            onSave(result.fields, result.resource, [file]);
        }
    }

    /** Life cycle
     ================================================================= */
    useEffect(() => {
        if (show && file) {
            extractDocument();
        }
    }, [show, file]);

    /** Render
     ================================================================= */
    const driverWarnings = isCatScale ? catScaleWarningSummary(items) : null;

    return (
        <>
            <OcrSplitDialog
                show={show}
                title={translate('btn.create_from_document')}
                file={file}
                isLoading={isLoading}
                isError={isError}
                onRetry={extractDocument}
                onClose={onClose}
                onSave={handleSave}
                saveLabel={translate('btn.save')}
                translate={translate}
                isBeta={true}
                renderPreview={csvRows ? () => <CsvPreviewTable rows={csvRows} translate={translate}/> : undefined}
                renderForm={() => (
                    <>
                        {!!driverWarnings?.count && (
                            <div className="px-6 pt-6 max-w-[900px] mx-auto">
                                <InfoParagraph type="warning">
                                    {translate(driverWarnings.isTruck ? 'text.cat_scale_unmatched_trucks' : 'text.cat_scale_unmatched_drivers', [driverWarnings.count, items.length])}

                                    {!!driverWarnings.names.length && (
                                        <span className="block mt-1">{driverWarnings.names.join(', ')}</span>
                                    )}
                                </InfoParagraph>
                            </div>
                        )}

                        <BillExpenseOcrForm
                            mode={mode}
                            setMode={setMode}
                            fields={fields}
                            setFields={setFields}
                            items={items}
                            setItems={setItems}
                            errorMessage={errorMessage}
                            translate={translate}
                            defaults={defaults}
                        />
                    </>
                )}
            />

            <ModalConfirm
                title={translate('text.Confirm')}
                show={noVendorFoundDialog}
                text={translate('text.OCR_no_vendor_found')}
                textClassName="text-sm text-tm-gray-900"
                buttonLabel={translate('btn.confirm')}
                translate={translate}
                onConfirm={() => setNoVendorFoundDialog(false)}
            />
        </>
    );
}
